import { useEffect, useRef } from "react";
import { LogOut, ShieldCheck } from "lucide-react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";

import { ThemeToggle } from "@/components/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAdminLogoutMutation, useAdminMeQuery } from "@/features/api";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Özet", end: true, superOnly: false },
  { to: "/organizers", label: "Organizatörler", end: false, superOnly: false },
  { to: "/orders", label: "Siparişler", end: false, superOnly: false },
  { to: "/members", label: "Kullanıcılar", end: false, superOnly: false },
  { to: "/payouts", label: "Ödemeler", end: false, superOnly: false },
  { to: "/payment-settings", label: "Ödeme ayarları", end: false, superOnly: false },
  { to: "/reports", label: "Raporlar", end: false, superOnly: false },
  { to: "/team", label: "Ekip", end: false, superOnly: true },
  { to: "/audit-logs", label: "Kayıtlar", end: false, superOnly: false },
];

export function AdminShell() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: me } = useAdminMeQuery();
  const logout = useAdminLogoutMutation();
  const navRef = useRef<HTMLElement>(null);

  // Mobilde menü yatay kayar; sayfa değişince aktif sekme görünür kalsın.
  useEffect(() => {
    navRef.current
      ?.querySelector('[aria-current="page"]')
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [pathname]);

  async function handleLogout() {
    try {
      await logout.mutateAsync();
    } finally {
      navigate("/login", { replace: true });
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-muted/30">
      <header className="sticky top-0 z-30 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-6 px-4 lg:flex-nowrap">
          <div className="flex h-14 shrink-0 items-center gap-2 font-semibold whitespace-nowrap">
            <ShieldCheck className="size-5 text-primary" />
            <span>TicketNjoy Admin</span>
          </div>
          <nav
            ref={navRef}
            className="order-last -mx-4 basis-[calc(100%+2rem)] scroll-px-4 overflow-x-auto pb-2 [scrollbar-width:none] lg:order-none lg:mx-0 lg:basis-auto lg:overflow-visible lg:pb-0"
          >
            <div className="flex w-max items-center gap-1 px-4 lg:px-0">
              {NAV.filter(
                (item) => !item.superOnly || me?.role === "SuperAdmin",
              ).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      "rounded-md px-3 py-1.5 text-sm whitespace-nowrap transition-colors",
                      isActive
                        ? "bg-muted font-medium text-foreground"
                        : "text-muted-foreground hover:text-foreground",
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </nav>
          <div className="ml-auto flex h-14 items-center gap-3">
            {me && (
              <div className="hidden items-center gap-2 text-sm whitespace-nowrap md:flex lg:hidden xl:flex">
                <span className="text-muted-foreground">{me.displayName}</span>
                <Badge
                  variant={me.role === "SuperAdmin" ? "default" : "secondary"}
                >
                  {me.role === "SuperAdmin" ? "Süper admin" : "Destek"}
                </Badge>
              </div>
            )}
            <ThemeToggle />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              disabled={logout.isPending}
            >
              <LogOut className="size-4" />
              Çıkış
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8">
        <Outlet />
      </main>
    </div>
  );
}
